/**
 * The invisible reCAPTCHA v3 loader shared by `apps/app` and `apps/admin`.
 *
 * Every failure path resolves `undefined` rather than throwing, so a captcha
 * problem degrades to "submit with no token" and the server decides whether
 * to accept the request.
 *
 * The site key comes from `oauth/provider-config-get`'s `recaptcha.siteKey` at
 * runtime and is passed in here. The action is `otp_request` because
 * `services/auth-api/src/lib/verifyCaptcha.ts` refuses any other action.
 */

export const RECAPTCHA_ACTION = "otp_request";
export const RECAPTCHA_SCRIPT_SRC_BASE =
  "https://www.google.com/recaptcha/api.js";

declare global {
  // An `interface` is the only thing TypeScript merges into the built-in
  // `Window`, so the repo's type-over-interface rule cannot apply here.
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string },
      ) => Promise<string>;
    };
  }
}

// The script is injected at most once per site key, even across repeated
// calls (a fan retrying after CAPTCHA_FAILED mints a fresh token from the
// already-loaded script rather than re-injecting it).
let loadedForSiteKey: string | null = null;
let loadPromise: Promise<void> | null = null;

function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (loadedForSiteKey === siteKey && loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve) => {
    const script = document.createElement("script");
    script.src = `${RECAPTCHA_SCRIPT_SRC_BASE}?render=${encodeURIComponent(siteKey)}`;
    script.addEventListener("load", () => {
      loadedForSiteKey = siteKey;
      resolve();
    });
    // A failed script load must not throw, per this module's own contract
    // above, it resolves same as a successful one, and the caller finds
    // `window.grecaptcha` still absent.
    script.addEventListener("error", () => {
      resolve();
    });
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Resolves an invisible reCAPTCHA v3 token, or `undefined` when `siteKey` is
 * missing, the "unset" sentinel, the script fails to load, or
 * `grecaptcha.execute` rejects. Never throws, never rejects.
 */
export async function getRecaptchaToken(
  siteKey: string | undefined,
): Promise<string | undefined> {
  if (siteKey === undefined || siteKey === "" || siteKey === "unset") {
    return undefined;
  }
  try {
    await loadRecaptchaScript(siteKey);
  } catch {
    return undefined;
  }
  if (typeof window === "undefined" || !window.grecaptcha) return undefined;
  // Captured into a local: TypeScript's narrowing above does not survive
  // into the closure below, since `window.grecaptcha` is a mutable property
  // access that could in principle change between now and then.
  const grecaptcha = window.grecaptcha;
  return new Promise<string | undefined>((resolve) => {
    grecaptcha.ready(() => {
      grecaptcha
        .execute(siteKey, { action: RECAPTCHA_ACTION })
        .then(resolve)
        .catch(() => {
          resolve(undefined);
        });
    });
  });
}

// The same bound the fan sign-in screen uses, so a slow or blocked Google never holds a sign-in button.
export const RECAPTCHA_TOKEN_TIMEOUT_MS = 3000;

/**
 * Resolves the token, or `undefined` once `timeoutMs` passes first. Never
 * rejects.
 */
export function getRecaptchaTokenWithin({
  siteKey,
  timeoutMs,
}: {
  siteKey: string | undefined;
  timeoutMs: number;
}): Promise<string | undefined> {
  return new Promise<string | undefined>((resolve) => {
    const timer = setTimeout(() => {
      resolve(undefined);
    }, timeoutMs);
    void getRecaptchaToken(siteKey).then((token) => {
      clearTimeout(timer);
      resolve(token);
    });
  });
}
